import { ENV } from '../../constants/environments';
import { createRef, useEffect, useState } from 'react';

import { CheckmarkIcon } from '../../modules/Icons';
import AddToWishlistButton from '../wishlists/AddToWishlistButton';
import { Tooltip } from 'react-tooltip';
import DownloadAndPurchaseButtonBlock from '../products/DownloadAndPurchaseButtonBlock';
import ProductPriceTag from '../products/ProductPriceTag';
import LicenseeTag from '../products/LicenseeTag';
import LicenseWarningTag from '../products/LicenseWarningTag';
import TagContainer from '../shared/TagContainer';

import { ProductOwnershipResponse } from '../../types/api/OwnershipTypes';

import { getClipPreviewSources } from '../../helpers/pages/collections/collectionsHelpers';
import purchaseHelpers from '../../helpers/purchaseHelpers';

const ProductGridItem = (props) => {
  const {
    product,
    collection,
    ownership,
    setSelectedProduct,
    setIsConfirmLicenseFlowOpen,
    hasLegitimateLicense,
    currentLicenseeProfile,
    isPfl,
    setEmailVerificationModalOpen,
    onProductPurchased = () => { },
    licenseTypes,
  } = props;
  const [hovered, setHovered] = useState(false);
  const [addToWishlistDropdownOpened, setAddToWishlistDropdownOpened] = useState(false);
  const [isVideoLandscape, setIsVideoLandscape] = useState(true);
  const [productOwned, setProductOwned] = useState(!!ownership);
  const previewTipId = 'product-tip-' + product.id;
  const videoRef: any = createRef<HTMLVideoElement>();
  const [needsRepurchase, setNeedsRepurchase] = useState(false);
  const { determineNeedsRepurchase, determinePurchaseLicenseName, determinePurchaseLicensedToName } = purchaseHelpers;

  useEffect(() => {
    setProductOwned(!!ownership);
    setNeedsRepurchase(determineNeedsRepurchase(ownership, collection));
  }, [ownership]);

  const onOwnershipDataLoaded = (ownershipData: ProductOwnershipResponse) => {
    if (onProductPurchased) onProductPurchased(ownershipData);
  }

  const determineLicensedToName = () => {
    if (!ownership) return '';
    return determinePurchaseLicensedToName(ownership);
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    };
    return date.toLocaleDateString('en-US', options);
  };

  const getPurchaseLicenseName = (): string => {
    if (!licenseTypes || !ownership) return '';
    return determinePurchaseLicenseName(licenseTypes, ownership);
  }

  useEffect(() => {
    const videoElement = videoRef.current;

    const checkVideoOrientation = () => {
      if (videoElement) {
        const videoWidth = videoElement.videoWidth;
        const videoHeight = videoElement.videoHeight;
        setIsVideoLandscape(videoWidth >= videoHeight);
      }
    };

    if (videoElement && videoElement.readyState >= 1) {
      checkVideoOrientation();
    } else {
      videoElement?.addEventListener('loadedmetadata', checkVideoOrientation);
    }

    return () => {
      if (videoElement) {
        videoElement.removeEventListener('loadedmetadata', checkVideoOrientation);
      }
    };
  }, [hovered]);

  const videoClass = isVideoLandscape
    ? 'shadow-md rounded-lg border border-[#4D5457] max-w-[480px] max-h-[600px]'
    : 'shadow-md rounded-lg border border-[#4D5457] max-w-[400px] max-h-[600px]';

  const renderLicenseTag = () => {
    if (needsRepurchase)
      return <LicenseWarningTag className="absolute bottom-2 right-2 z-[20]" productId={product.id} />
    if (
      hasLegitimateLicense && !collection.free_product ||
      currentLicenseeProfile && !currentLicenseeProfile?.invalidated
    )
      return <LicenseeTag className="absolute z-20 bottom-2 right-2" productId={product.id} />
    return null;
  }

  return (
    <div
      className="text-a-lighter-gray stroke-a-lighter-gray hover:text-white hover:stroke-white"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      <div
        data-tooltip
        data-tooltip-id={previewTipId}
        className="relative pb-[56.25%] rounded-[5px] border border-transparent hover:border-[#4D5457] hover:shadow-md"
        onClick={() => setSelectedProduct(product)}>
        {productOwned && renderLicenseTag()}
        {
          licenseTypes &&
          <>
            <Tooltip
              id={product.id}
              place='top'
              opacity={1}
              className=" rounded-[5px] gap-1 leading-[22px] !text-[13px] normal-case font-normal whitespace-normal !z-[99]"
              style={{ backgroundColor: '#15191B' }}>
              License: <span className='font-light text-a-gray'>{getPurchaseLicenseName()}</span>
              <br></br>
              Licensed to: <span className='font-light text-a-gray'>{determineLicensedToName()}</span>
            </Tooltip>
            <Tooltip
              id={'license-warning-' + product.id}
              place='top'
              opacity={1}
              className=" rounded-[5px] !text-[14px] normal-case font-normal whitespace-normal !z-[99]"
              style={{ backgroundColor: '#15191B', maxWidth: '300px' }}>
              <div className='mb-1'>License Upgrade Required</div>
              <span className='font-light text-a-gray !text-[12px]'>This product was purchased on {formatDate(ownership?.created_at)} under the {getPurchaseLicenseName()} license. Purchase the item with your current {currentLicenseeProfile?.name} license to download.</span>
            </Tooltip>
          </>
        }

        <div className="absolute top-0 right-0 w-full h-full">
          <img
            className="rounded-[4px] w-full h-full"
            src={ENV.uploadsUrl + product.poster}
            alt={collection.name + ' ' + product.name + ' vfx asset stock footage'}
            loading="lazy"
          />

          <TagContainer
            free={product.free_product}
            containerClasses={
              'absolute flex gap-1 top-[12px] left-[12px] text-white text-[10px] uppercase z-10'
            }
          />

          {productOwned && !needsRepurchase && !collection.owned_through_subscription && (
            <div className="absolute top-3 right-3">
              <CheckmarkIcon className="w-4 h-4 stroke-a-green" />
            </div>
          )}

          {(hovered || addToWishlistDropdownOpened) && (
            <div className="absolute top-0 w-full h-full flex items-center justify-center drop-shadow-md text-[14px] font-extralight">
              Click for details
            </div>
          )}
          {!productOwned && <ProductPriceTag product={product} collection={collection} className="absolute top-2 left-2" />}
          <Tooltip
            id={previewTipId}
            place="right"
            noArrow={true}
            offset={0}
            style={{
              background: 'transparent',
              zIndex: 50
            }}
            opacity={1}
            afterShow={async () => { }}
            afterHide={() => { }}>
            <div className={isVideoLandscape ? '' : 'items-center justify-center mb-96'}>
              <video
                poster={product.video.poster}
                ref={videoRef}
                className={`hidden md:block ${videoClass}`}
                preload="none"
                loop
                autoPlay
                muted>
                { getClipPreviewSources(product).map((source, k) => <source key={'video_' + k} src={source.src} type={source.type} /> )}
              </video>
            </div>
          </Tooltip>
        </div>
      </div>
      <div className="flex justify-items-center items-center align-center gap-2 pt-1 h-[42px] mt-1">

        <h3
          className="grow text-[11px] md:text-[12px] xl:text-[13px] leading-tight font-[Inter]"
          onClick={() => setSelectedProduct(product)}>
          <span className="line-clamp-2 hover:line-clamp-4">{product.name}</span>
        </h3>

        <button className="z-20 hover:stroke-a-blue">
          <AddToWishlistButton
            type="icon"
            product={product}
            onWishlistDropdownToggle={setAddToWishlistDropdownOpened}
            popoverClassName="max-md:w-[calc(100vw-48px)] max-md:right-6 !max-md:ml-0"
            parentCollectionId={collection.id}
          />
        </button>
        <div className="mt-1">
          <DownloadAndPurchaseButtonBlock
            mode="product-grid-item"
            onPurchased={() => {
              setProductOwned(true)
            }}
            onOwnershipDataLoaded={onOwnershipDataLoaded}
            downloads={ownership?.downloads || product?.variant_downloads}
            productItem={product}
            collectionId={collection.id}
            setIsConfirmLicenseFlowOpen={setIsConfirmLicenseFlowOpen}
            setSelectedProduct={setSelectedProduct}
            ownership={ownership}
            needsRepurchase={needsRepurchase}
            isPfl={isPfl}
            setEmailVerificationModalOpen={setEmailVerificationModalOpen}
          />
        </div>
      </div>
    </div>
  );
}

export default ProductGridItem;
